import React, { useContext } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { MEDIA } from '@constants';
import { CartItem, ProductCode } from '@contracts';
import { createSimpleCartItemForProductCode, getCartItemType } from '@utils/cart';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';
import Checkbox from '../Checkbox';
import PriceDisplay from '../PriceDisplay';
import SimpleBox from '../SimpleBox';
import { TEXT_STYLES, TextVariant } from './Typography';
import {
  getShoppingAddonBadgeText,
  getShoppingAddonBadgeType,
  getShoppingAddonTitle,
  getShoppingAddonPriceSuffix,
  getShoppingAddonIcons,
  getShoppingAddonSubtitle,
  getShoppingAddonIsHighlighted
} from './utils/shoppingAddons';

interface Props {
  productCode: ProductCode;
  price: number;
  addonChecked: boolean;
  handleCheckboxClick: (cartItem: CartItem) => void;
}

const ShoppingAddon: React.FC<Props> = ({ productCode, price, addonChecked, handleCheckboxClick }) => {
  const { state } = useContext(Context);
  const { currency, country } = state;
  const { t } = useTranslation();
  const quantity = 1;
  const editable = true;
  const cartItemType = getCartItemType(productCode);
  const cartItem = Number(price) > -1 ? createSimpleCartItemForProductCode(productCode, quantity, price, editable, cartItemType) : null;
  const bottomIcons = getShoppingAddonIcons(productCode, country);
  const descriptions = getShoppingAddonSubtitle(productCode);
  const isHighlighted = getShoppingAddonIsHighlighted(productCode);
  const addonTitle = t(getShoppingAddonTitle(productCode));
  const addonTitleTextRef = useReplacedInnerHtml(addonTitle);

  return (
    <SimpleBox
      key={productCode}
      badgeText={t(getShoppingAddonBadgeText(productCode))}
      badgeType={getShoppingAddonBadgeType(productCode)}
      onClick={() => (cartItem ? handleCheckboxClick(cartItem) : null)}
      isHighlighted={isHighlighted}
      selectable
      selected={addonChecked}
    >
      <AddOnItem>
        <ItemTopWrapper>
          {cartItem ? (
            <CheckboxContainer>
              <Checkbox checked={addonChecked} design="compact" />
            </CheckboxContainer>
          ) : null}
          <ProductTitle ref={addonTitleTextRef} />
          <PriceDisplay price={price} priceSuffix={getShoppingAddonPriceSuffix(productCode, country)} includePlusSign />
        </ItemTopWrapper>
        {bottomIcons.length || descriptions.length ? (
          <ItemBottomWrapper>
            {bottomIcons.length ? (
              <BottomIcons>
                {bottomIcons.map((icon, index) => (
                  <BottomIcon key={index} src={icon} $index={index} />
                ))}
              </BottomIcons>
            ) : null}
            {descriptions.length ? (
              <BottomDescriptions>
                {descriptions.map((description, index) => (
                  <Description key={index}>{t(description)}</Description>
                ))}
              </BottomDescriptions>
            ) : null}
          </ItemBottomWrapper>
        ) : null}
      </AddOnItem>
    </SimpleBox>
  );
};

export default ShoppingAddon;

const AddOnItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  align-items: flex-start;

  & > div {
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
`;

const ItemTopWrapper = styled.div``;

const ItemBottomWrapper = styled.div``;

const BottomDescriptions = styled.div``;

const BottomIcons = styled.div`
  display: flex;
  height: 40px;
`;

const BottomIcon = styled.img<{ $index: number }>`
  width: 40px;
  max-width: 40px;
  height: 100%;
  border: 1px solid ${Colors.babyBlue};
  border-radius: 50%;
  margin-left: -${(props) => props.$index * 16}px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  cursor: pointer;
  user-select: none;
`;

const ProductTitle = styled.div`
  font-family: 'BrownStd';
  color: ${Colors.navyBlue};
  font-size: 16px;
  font-weight: 600;
  font-style: normal;
  text-align: left;
  flex: 1;

  ${MEDIA.laptop} {
    font-size: 15px;
  }
`;

const Description = styled.div`
  ${TEXT_STYLES[TextVariant.BodyRegularSM]}
  text-align: left;

  &:empty {
    display: none;
  }
`;
